/**
 * VenueFlow Hub
 * 
 * Central launcher for all VenueFlow deployments.
 * Shows accessible club bases as tiles after login.
 * 
 * User types:
 *   - Club user: sees their club only (auto-redirects)
 *   - Club Partner (MKTG/KOJO): sees all clubs, uploaders + library only
 *   - Commercial partner: sees granted clubs
 *   - Lumen8 staff: sees everything
 * 
 * Deploy as its own Vercel app (e.g., app.lumen8venueflow.com)
 * pointing at the same Supabase project.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useDeployments } from "../hooks/useDeployments";

// ============================================================
// GRADIENT CONSTANTS (matches club portal branding)
// ============================================================
const GRADIENT = "linear-gradient(135deg, #00e0c0 0%, #2fd6b5 25%, #5cc86e 50%, #84cc16 75%, #c8e600 100%)";
const GRADIENT_SUBTLE = "linear-gradient(135deg, rgba(47, 214, 181, 0.7) 0%, rgba(132, 204, 22, 0.7) 100%)";

// ============================================================
// STYLES
// ============================================================
const styles = {
  page: {
    minHeight: "100vh",
    backgroundColor: "#0c0c0f",
    fontFamily: "'Space Grotesk', -apple-system, sans-serif",
    color: "#fff",
  },

  // ── Header ──
  headerWrap: {
    position: "relative",
  },
  header: {
    padding: "20px 32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    position: "relative",
    zIndex: 1,
  },
  headerGlow: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: 1,
    background: GRADIENT,
    opacity: 0.6,
  },
  headerGlowBlur: {
    position: "absolute",
    bottom: -4,
    left: "5%",
    right: "5%",
    height: 8,
    background: GRADIENT,
    opacity: 0.15,
    filter: "blur(8px)",
  },
  headerLeft: {
    display: "flex",
    alignItems: "flex-start",
    gap: 3,
    flexDirection: "column",
  },
  logoImage: {
    height: 28,
    width: "auto",
    objectFit: "contain",
  },
  venueflowText: {
    fontSize: 11,
    fontWeight: 600,
    background: GRADIENT_SUBTLE,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    letterSpacing: "4px",
    textTransform: "uppercase",
    marginTop: 6,
    marginLeft: 2,
    lineHeight: 1,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  userInfo: {
    textAlign: "right",
  },
  userName: {
    fontSize: 13,
    fontWeight: 600,
    background: GRADIENT,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  userRole: {
    fontSize: 11,
    color: "rgba(255,255,255,0.35)",
    marginTop: 2,
  },
  signOutBtn: {
    padding: "6px 14px",
    fontSize: 12,
    fontWeight: 600,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 6,
    cursor: "pointer",
    fontFamily: "inherit",
    color: "#5ee8c5",
  },

  // ── Content ──
  content: {
    maxWidth: 1000,
    margin: "0 auto",
    padding: "48px 32px 64px",
  },
  welcome: {
    marginBottom: 40,
  },
  welcomeTitle: {
    fontSize: 28,
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 8px",
    letterSpacing: "-0.02em",
  },
  welcomeName: {
    background: GRADIENT,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  welcomeSub: {
    fontSize: 15,
    color: "rgba(255,255,255,0.4)",
    margin: 0,
    lineHeight: 1.5,
  },

  // ── Grid ──
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 16,
  },

  // ── Deployment tile ──
  tile: {
    backgroundColor: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.12)",
    borderRadius: 14,
    padding: 0,
    cursor: "pointer",
    transition: "all 0.25s ease",
    overflow: "hidden",
    textDecoration: "none",
    display: "block",
    position: "relative",
  },
  tileHover: {
    backgroundColor: "rgba(255,255,255,0.06)",
    borderColor: "#2fd6b5",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.3), 0 0 20px rgba(47, 214, 181, 0.08)",
  },
  tileAccent: {
    height: 3,
    width: "100%",
  },
  tileBody: {
    padding: "20px 22px 18px",
  },
  tileTop: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 14,
  },
  tileLogo: {
    width: 44,
    height: 44,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    fontWeight: 800,
    color: "#fff",
    flexShrink: 0,
    overflow: "hidden",
  },
  tileLogoImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  tileRoleBadge: {
    fontSize: 10,
    fontWeight: 600,
    padding: "3px 8px",
    borderRadius: 4,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  tileName: {
    fontSize: 16,
    fontWeight: 600,
    color: "#fff",
    margin: "0 0 4px",
  },
  tileDescription: {
    fontSize: 13,
    color: "rgba(255,255,255,0.4)",
    margin: "0 0 14px",
  },
  tileVenues: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
  },
  venueTag: {
    fontSize: 11,
    fontWeight: 500,
    padding: "3px 10px",
    borderRadius: 4,
    backgroundColor: "rgba(255,255,255,0.06)",
    color: "rgba(255,255,255,0.5)",
  },
  tileArrow: {
    position: "absolute",
    bottom: 18,
    right: 20,
    color: "rgba(255,255,255,0.15)",
    transition: "all 0.25s ease",
  },
  tileArrowHover: {
    color: "#2fd6b5",
    transform: "translateX(3px)",
  },

  // ── States ──
  loading: {
    textAlign: "center",
    padding: "80px 0",
    color: "rgba(255,255,255,0.3)",
    fontSize: 14,
  },
  spinner: {
    width: 24,
    height: 24,
    border: "2px solid rgba(255,255,255,0.1)",
    borderTopColor: "#2fd6b5",
    borderRadius: "50%",
    margin: "0 auto 16px",
    animation: "spin 0.8s linear infinite",
  },
  empty: {
    textAlign: "center",
    padding: "80px 32px",
    color: "rgba(255,255,255,0.3)",
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "rgba(255,255,255,0.5)",
    margin: "0 0 8px",
  },
  emptyText: {
    fontSize: 14,
    margin: 0,
    lineHeight: 1.5,
  },
  errorBox: {
    padding: "16px 20px",
    backgroundColor: "rgba(239, 68, 68, 0.1)",
    border: "1px solid rgba(239, 68, 68, 0.2)",
    borderRadius: 10,
    color: "#fca5a5",
    fontSize: 14,
    marginBottom: 24,
  },

  // ── Welcome Modal ──
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.75)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(4px)",
  },
  modalContent: {
    backgroundColor: "#1a1a1f",
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.1)",
    padding: "36px 40px",
    maxWidth: 520,
    width: "90%",
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.4)",
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: "#fff",
    margin: "0 0 24px",
    lineHeight: 1.3,
  },
  modalTitleAccent: {
    background: GRADIENT,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  modalBody: {
    fontSize: 15,
    color: "rgba(255,255,255,0.6)",
    lineHeight: 1.8,
    margin: "0 0 28px",
  },
  modalTagline: {
    display: "block",
    fontSize: 14,
    fontWeight: 600,
    color: "rgba(255,255,255,0.8)",
    fontStyle: "italic",
    marginTop: 20,
  },
  modalFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  modalCheckbox: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
  },
  modalCheckboxInput: {
    width: 16,
    height: 16,
    accentColor: "#2fd6b5",
    cursor: "pointer",
  },
  modalCheckboxLabel: {
    fontSize: 13,
    color: "rgba(255,255,255,0.4)",
  },
  modalButton: {
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 600,
    color: "#0c0c0f",
    background: GRADIENT,
    border: "none",
    borderRadius: 8,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
  },
};

// Keyframes (injected once)
const injectStyles = () => {
  if (document.getElementById("hub-keyframes")) return;
  const style = document.createElement("style");
  style.id = "hub-keyframes";
  style.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700;800&display=swap');
  `;
  document.head.appendChild(style);
};

// ============================================================
// ROLE BADGE (on tiles)
// ============================================================
const roleBadgeStyle = (role) => {
  switch (role) {
    case "lumen8":
      return { backgroundColor: "rgba(47, 214, 181, 0.15)", color: "#2fd6b5" };
    case "commercial":
      return { backgroundColor: "rgba(251, 191, 36, 0.15)", color: "#fbbf24" };
    case "producer":
      return { backgroundColor: "rgba(168, 85, 247, 0.15)", color: "#a855f7" };
    case "club":
    default:
      return { backgroundColor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)" };
  }
};

const roleLabel = (role) => {
  switch (role) {
    case "lumen8": return "Lumen8";
    case "commercial": return "Partner";
    case "producer": return "Club Partner";
    case "club": return "Club";
    default: return role;
  }
};

// User-facing role display for header
const getUserRoleDisplay = (isLumen8Staff, isProducer) => {
  if (isLumen8Staff) return "Lumen8 Operator";
  if (isProducer) return "Club Partner";
  return "Club Member";
};

// ============================================================
// WELCOME MODAL
// ============================================================
const WELCOME_DISMISSED_KEY = "venueflow_hub_welcome_dismissed";

function WelcomeModal({ onClose, name }) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    if (dontShowAgain) {
      localStorage.setItem(WELCOME_DISMISSED_KEY, "true");
    }
    onClose();
  };

  return (
    <div style={styles.modalOverlay} onClick={handleClose}>
      <div 
        style={styles.modalContent} 
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={styles.modalTitle}>
          Hi <span style={styles.modalTitleAccent}>{name}</span>!
          <br />
          Welcome to <span style={styles.modalTitleAccent}>Lumen8 VenueFlow</span>.
        </h2>
        <p style={styles.modalBody}>
          <strong style={{ color: "rgba(255,255,255,0.85)" }}>Stadium content delivery, streamlined.</strong> Upload your assets, assign them to venues, events and playout systems, and track every file from submission through to approval for live playout.
          <span style={styles.modalTagline}>One platform. Every screen. Matchday ready.</span>
        </p>
        <div style={styles.modalFooter}>
          <label style={styles.modalCheckbox}>
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              style={styles.modalCheckboxInput}
            />
            <span style={styles.modalCheckboxLabel}>Don't show again</span>
          </label>
          <button
            style={styles.modalButton}
            onClick={handleClose}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-1px)";
              e.target.style.boxShadow = "0 4px 12px rgba(47, 214, 181, 0.3)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "none";
            }}
          >
            OK, let's go!
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// DEPLOYMENT TILE
// ============================================================
function DeploymentTile({ deployment, session }) {
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    // Build URL with session tokens for SSO
    const targetUrl = new URL(deployment.deployment_url);
    if (session?.access_token) {
      targetUrl.searchParams.set('access_token', session.access_token);
      targetUrl.searchParams.set('refresh_token', session.refresh_token);
    }
    window.location.href = targetUrl.toString();
  };

  const venues = Array.isArray(deployment.venues) ? deployment.venues : [];
  const accentColor = deployment.accent_color || "#2fd6b5";

  return (
    <div
      style={{
        ...styles.tile,
        ...(hovered ? styles.tileHover : {}),
      }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="link"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      {/* Accent bar */}
      <div
        style={{
          ...styles.tileAccent,
          background: `linear-gradient(90deg, ${accentColor}, ${accentColor}88)`,
        }}
      />

      <div style={styles.tileBody}>
        {/* Top row: logo + role badge */}
        <div style={styles.tileTop}>
          <div style={styles.tileLogo}>
            {deployment.logo_url ? (
              <img
                src={deployment.logo_url}
                alt={deployment.club_code}
                style={styles.tileLogoImg}
              />
            ) : (
              deployment.club_code
            )}
          </div>
          <span
            style={{
              ...styles.tileRoleBadge,
              ...roleBadgeStyle(deployment.user_role),
            }}
          >
            {roleLabel(deployment.user_role)}
          </span>
        </div>

        {/* Name + description */}
        <h3 style={styles.tileName}>{deployment.display_name}</h3>
        {deployment.description && (
          <p style={styles.tileDescription}>{deployment.description}</p>
        )}

        {/* Venue tags */}
        {venues.length > 0 && (
          <div style={styles.tileVenues}>
            {venues.map((v) => (
              <span key={v} style={styles.venueTag}>{v}</span>
            ))}
          </div>
        )}

        {/* Arrow */}
        <div
          style={{
            ...styles.tileArrow,
            ...(hovered ? styles.tileArrowHover : {}),
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HUB PAGE
// ============================================================
export default function HubPage() {
  const { user, userName, userEmail, isLumen8Staff, isProducer, allowedClubs, signOut, session } = useAuth();
  const { deployments: rawDeployments, loading, error } = useDeployments(user);
  const [showWelcome, setShowWelcome] = useState(false);

  // Filter deployments for producers with allowed_clubs restriction
  const deployments = useMemo(() => {
    // Lumen8 staff see all
    if (isLumen8Staff) return rawDeployments;
    
    // Producers with allowed_clubs: filter to their clubs only
    if (isProducer && allowedClubs && Array.isArray(allowedClubs) && allowedClubs.length > 0) {
      return rawDeployments.filter(d => 
        allowedClubs.some(club => 
          club.toUpperCase() === d.club_code?.toUpperCase() ||
          club.toUpperCase() === d.club_id?.toUpperCase()
        )
      );
    }
    
    // Everyone else sees what RLS allows
    return rawDeployments;
  }, [rawDeployments, isLumen8Staff, isProducer, allowedClubs]);

  useEffect(() => {
    injectStyles();
    // Check if welcome modal should show
    const dismissed = localStorage.getItem(WELCOME_DISMISSED_KEY);
    if (!dismissed) {
      setShowWelcome(true);
    }
  }, []);

  // Auto-redirect: if user has exactly 1 deployment, go straight there
  useEffect(() => {
    if (!loading && deployments.length === 1 && !isLumen8Staff) {
      // Build URL with session tokens for SSO
      const targetUrl = new URL(deployments[0].deployment_url);
      if (session?.access_token) {
        targetUrl.searchParams.set('access_token', session.access_token);
        targetUrl.searchParams.set('refresh_token', session.refresh_token);
      }
      window.location.href = targetUrl.toString();
    }
  }, [loading, deployments, isLumen8Staff, session]);

  const displayName = userName || userEmail?.split("@")[0] || "there";
  const userRoleDisplay = getUserRoleDisplay(isLumen8Staff, isProducer);

  return (
    <div style={styles.page}>
      {/* Header with green glow */}
      <div style={styles.headerWrap}>
        <header style={styles.header}>
          <div style={styles.headerLeft}>
            <img 
              src="/lumen8-logo.png" 
              alt="Lumen8" 
              style={styles.logoImage}
            />
            <div style={styles.venueflowText}>VenueFlow</div>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.userInfo}>
              <div style={styles.userName}>{userName || userEmail}</div>
              <div style={styles.userRole}>{userRoleDisplay}</div>
            </div>
            <button
              style={styles.signOutBtn}
              onClick={signOut}
            >
              Sign Out
            </button>
          </div>
        </header>
        {/* Green gradient line under header */}
        <div style={styles.headerGlow} />
        <div style={styles.headerGlowBlur} />
      </div>

      {/* Content */}
      <main style={styles.content}>
        <div style={styles.welcome}>
          <h2 style={styles.welcomeTitle}>
            Welcome back, <span style={styles.welcomeName}>{displayName}</span>
          </h2>
          <p style={styles.welcomeSub}>
            {isLumen8Staff
              ? "Select a club below to manage their content and matchday operations."
              : "Select your club below to get started with uploading and managing content."}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div style={styles.errorBox}>
            Failed to load deployments: {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={styles.loading}>
            <div style={styles.spinner} />
            Loading your venues...
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && deployments.length === 0 && (
          <div style={styles.empty}>
            <p style={styles.emptyTitle}>No venues assigned</p>
            <p style={styles.emptyText}>
              Your account doesn't have access to any VenueFlow deployments yet.
              <br />
              Contact your Lumen8 administrator to get access.
            </p>
          </div>
        )}

        {/* Deployment grid */}
        {!loading && deployments.length > 0 && (
          <div style={styles.grid}>
            {deployments.map((d) => (
              <DeploymentTile key={d.id} deployment={d} session={session} />
            ))}
          </div>
        )}
      </main>

      {/* Welcome Modal */}
      {showWelcome && <WelcomeModal onClose={() => setShowWelcome(false)} name={displayName} />}
    </div>
  );
}
